import { CartLinesUpdateEvent } from '@shopify/events';

const PROTECTION_EVENT = 'pokegeek:cart-contract-updated';
const INITIAL_DATA_ID = 'pokegeek-cart-contract-data';
const PROTECTION_LINE_PROPERTY = '_pg_shipping_protection';

const TRANSLATIONS = Object.freeze({
  fr: Object.freeze({
    title: 'Protection d’expédition facultative',
    short: 'Couvre le vol du colis après une livraison officiellement enregistrée par le transporteur.',
    details_label: 'Détails de la couverture',
    details: 'En cas de dossier accepté, remboursement du montant TTC effectivement payé pour les seuls articles concernés, après remises. Les frais de livraison et le prix de la protection sont exclus. Aucune réexpédition n’est prévue. Vos droits légaux restent inchangés.',
    unavailable_range: 'Protection indisponible : le montant éligible dépasse la plage prise en charge.',
    unavailable_generic: 'Protection indisponible pour ce panier.',
    preview: 'Aperçu interne — ajout au panier désactivé.',
    mutation_error: 'La protection d’expédition n’a pas pu être vérifiée. Veuillez réessayer avant de poursuivre.',
  }),
  en: Object.freeze({
    title: 'Optional shipping protection',
    short: 'Covers theft of the parcel after delivery has been officially recorded by the carrier.',
    details_label: 'Coverage details',
    details: 'If a claim is accepted, reimbursement is limited to the amount including tax actually paid for the affected items only, after discounts. Shipping costs and the price of the protection are excluded. No replacement shipment is provided. Your statutory rights remain unchanged.',
    unavailable_range: 'Protection unavailable: the eligible amount exceeds the supported range.',
    unavailable_generic: 'Protection is unavailable for this cart.',
    preview: 'Internal preview — adding to cart is disabled.',
    mutation_error: 'Shipping protection could not be verified. Please try again before continuing.',
  }),
  es: Object.freeze({
    title: 'Protección de envío opcional',
    short: 'Cubre el robo del paquete después de que el transportista haya registrado oficialmente la entrega.',
    details_label: 'Detalles de la cobertura',
    details: 'Si se acepta el expediente, se reembolsará únicamente el importe con impuestos efectivamente pagado por los artículos afectados, una vez aplicados los descuentos. Se excluyen los gastos de envío y el precio de la protección. No se realizará ningún reenvío. Sus derechos legales permanecen inalterados.',
    unavailable_range: 'Protección no disponible: el importe elegible supera el rango admitido.',
    unavailable_generic: 'La protección no está disponible para este carrito.',
    preview: 'Vista previa interna — la adición al carrito está desactivada.',
    mutation_error: 'No se pudo verificar la protección de envío. Inténtelo de nuevo antes de continuar.',
  }),
  de: Object.freeze({
    title: 'Optionale Versandabsicherung',
    short: 'Deckt den Diebstahl des Pakets ab, nachdem die Zustellung vom Versanddienstleister offiziell erfasst wurde.',
    details_label: 'Details zur Absicherung',
    details: 'Bei Annahme des Falls wird ausschließlich der tatsächlich gezahlte Bruttobetrag der betroffenen Artikel nach Abzug von Rabatten erstattet. Versandkosten und der Preis der Absicherung sind ausgeschlossen. Eine erneute Zusendung ist nicht vorgesehen. Ihre gesetzlichen Rechte bleiben unverändert.',
    unavailable_range: 'Absicherung nicht verfügbar: Der berechtigte Betrag liegt außerhalb des unterstützten Bereichs.',
    unavailable_generic: 'Die Absicherung ist für diesen Warenkorb nicht verfügbar.',
    preview: 'Interne Vorschau — Hinzufügen zum Warenkorb ist deaktiviert.',
    mutation_error: 'Die Versandabsicherung konnte nicht geprüft werden. Bitte versuchen Sie es erneut.',
  }),
  it: Object.freeze({
    title: 'Protezione spedizione facoltativa',
    short: 'Copre il furto del pacco dopo che la consegna è stata registrata ufficialmente dal trasportatore.',
    details_label: 'Dettagli della copertura',
    details: 'In caso di pratica accettata, viene rimborsato esclusivamente l’importo IVA inclusa effettivamente pagato per i soli articoli interessati, dopo gli sconti. Le spese di spedizione e il prezzo della protezione sono esclusi. Non è prevista alcuna nuova spedizione. I Suoi diritti legali restano invariati.',
    unavailable_range: 'Protezione non disponibile: l’importo idoneo supera l’intervallo supportato.',
    unavailable_generic: 'La protezione non è disponibile per questo carrello.',
    preview: 'Anteprima interna — aggiunta al carrello disattivata.',
    mutation_error: 'Impossibile verificare la protezione della spedizione. Riprovi prima di continuare.',
  }),
  nl: Object.freeze({
    title: 'Optionele verzendbescherming',
    short: 'Dekt diefstal van het pakket nadat de bezorging officieel door de vervoerder is geregistreerd.',
    details_label: 'Dekkingsdetails',
    details: 'Bij aanvaarding van het dossier wordt uitsluitend het werkelijk betaalde bedrag inclusief btw voor de betrokken artikelen terugbetaald, na toepassing van kortingen. Verzendkosten en de prijs van de bescherming zijn uitgesloten. Er vindt geen nieuwe verzending plaats. Uw wettelijke rechten blijven ongewijzigd.',
    unavailable_range: 'Bescherming niet beschikbaar: het in aanmerking komende bedrag overschrijdt het ondersteunde bereik.',
    unavailable_generic: 'Bescherming is niet beschikbaar voor deze winkelwagen.',
    preview: 'Interne voorvertoning — toevoegen aan winkelwagen is uitgeschakeld.',
    mutation_error: 'De verzendbescherming kon niet worden gecontroleerd. Probeer het opnieuw voordat u doorgaat.',
  }),
});

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeId(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function normalizeIdList(values) {
  return Object.freeze(Array.isArray(values) ? values.map(normalizeId).filter(Boolean) : []);
}

function normalizeStringList(values, { lowercase = false } = {}) {
  const normalized = (Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .map((value) => lowercase ? value.toLowerCase() : value);
  return Object.freeze([...new Set(normalized)]);
}

function normalizeProtectionUiConfig(value) {
  const source = isObject(value) ? value : {};
  return Object.freeze({
    enabled: source.enabled === true,
    preview_only: source.preview_only === true,
  });
}

function normalizeProtectionRuntimeConfig(data) {
  const source = isObject(data) ? data : {};
  const ui = isObject(source.protection_ui) ? source.protection_ui : source;
  const trusted = isObject(source.trusted_config) ? source.trusted_config : source;
  return Object.freeze({
    ...normalizeProtectionUiConfig(ui),
    protection_product_ids: normalizeIdList(trusted.protection_product_ids),
    protection_variant_ids: normalizeIdList(trusted.protection_variant_ids),
    protection_sku_prefixes: normalizeStringList([
      'PG-PROT-',
      ...(Array.isArray(trusted.protection_sku_prefixes) ? trusted.protection_sku_prefixes : []),
    ]),
    legacy_protection_product_ids: normalizeIdList(trusted.legacy_protection_product_ids),
    legacy_protection_variant_ids: normalizeIdList(trusted.legacy_protection_variant_ids),
    legacy_protection_skus: normalizeStringList(trusted.legacy_protection_skus, { lowercase: true }),
    legacy_protection_handles: normalizeStringList(trusted.legacy_protection_handles, { lowercase: true }),
  });
}

function expectedProtectionVariantId(quote, variantIds) {
  if (!isObject(quote) || quote.supported !== true) return null;
  if (!Number.isSafeInteger(quote.tier_index) || quote.tier_index < 0) return null;
  const ids = normalizeIdList(variantIds);
  return ids[quote.tier_index] ?? null;
}

function linePropertyIsTrue(line, propertyName) {
  if (!isObject(line?.properties)) return false;
  return String(line.properties[propertyName] ?? '').toLowerCase() === 'true';
}

function isInternalProtectionLine(line, runtimeConfig) {
  if (!isObject(line)) return false;
  const productId = normalizeId(line.product_id);
  const variantId = normalizeId(line.variant_id);
  const sku = String(line.sku || '').trim();
  return runtimeConfig.protection_product_ids.includes(productId)
    || runtimeConfig.protection_variant_ids.includes(variantId)
    || line?.classification?.protection === true
    || linePropertyIsTrue(line, PROTECTION_LINE_PROPERTY)
    || runtimeConfig.protection_sku_prefixes.some((prefix) => sku.startsWith(prefix));
}

function isLegacyProtectionLine(line, runtimeConfig) {
  if (!isObject(line)) return false;
  const productId = normalizeId(line.product_id);
  const variantId = normalizeId(line.variant_id);
  const sku = String(line.sku || '').trim().toLowerCase();
  const handle = String(line.product_handle || '').trim().toLowerCase();
  return runtimeConfig.legacy_protection_product_ids.includes(productId)
    || runtimeConfig.legacy_protection_variant_ids.includes(variantId)
    || runtimeConfig.legacy_protection_skus.includes(sku)
    || runtimeConfig.legacy_protection_handles.includes(handle)
    || line?.classification?.legacy_protection === true;
}

function inspectedLineGroup(lines) {
  return Object.freeze({
    count: lines.length,
    lines: Object.freeze(lines),
    keys: Object.freeze(lines.map((line) => String(line.key))),
    variant_ids: Object.freeze(lines.map((line) => normalizeId(line.variant_id))),
    quantities: Object.freeze(lines.map((line) => Number.isSafeInteger(line.quantity) ? line.quantity : 0)),
  });
}

function inspectShippingProtectionLines(contract, config) {
  const runtimeConfig = normalizeProtectionRuntimeConfig(config);
  const contractLines = Array.isArray(contract?.lines) ? contract.lines : [];
  const internal = contractLines.filter((line) => isInternalProtectionLine(line, runtimeConfig));
  const legacy = contractLines.filter((line) => isLegacyProtectionLine(line, runtimeConfig));
  return Object.freeze({
    internal: inspectedLineGroup(internal),
    legacy: inspectedLineGroup(legacy),
    count: internal.length + legacy.length,
    keys: Object.freeze([...internal, ...legacy].map((line) => String(line.key))),
  });
}

function inspectProtectionLines(contract, config) {
  return inspectShippingProtectionLines(contract, config).internal;
}

function mutationPlan(
  action,
  expectedVariantId,
  existingLineKeys,
  quantity,
  reason,
  { removeLineKeys = existingLineKeys, addExpected = false } = {}
) {
  return Object.freeze({
    action,
    expected_variant_id: expectedVariantId,
    existing_line_keys: Object.freeze([...existingLineKeys]),
    remove_line_keys: Object.freeze([...new Set(removeLineKeys.map(String))]),
    add_expected: addExpected === true,
    quantity,
    reason,
  });
}

function planShippingProtectionNormalization({
  contract,
  config,
  optedIn = false,
  voluntarilyDisabled = false,
  mutationInProgress = false,
} = {}) {
  const runtimeConfig = normalizeProtectionRuntimeConfig(config);
  const inspection = inspectShippingProtectionLines(contract, config);
  const quote = isObject(contract?.protection_quote) ? contract.protection_quote : null;
  const expectedVariantId = expectedProtectionVariantId(quote, runtimeConfig.protection_variant_ids);
  const allKeys = inspection.keys;

  if (mutationInProgress) {
    return mutationPlan('none', expectedVariantId, allKeys, 1, 'mutation_in_progress');
  }
  if (!runtimeConfig.enabled) {
    return inspection.count > 0
      ? mutationPlan('remove', null, allKeys, 0, 'disabled')
      : mutationPlan('none', null, allKeys, 0, 'disabled');
  }
  if (voluntarilyDisabled) {
    return inspection.count > 0
      ? mutationPlan('remove', null, allKeys, 0, 'voluntarily_disabled')
      : mutationPlan('none', null, allKeys, 0, 'already_disabled');
  }
  if (!quote || quote.supported !== true || expectedVariantId === null) {
    return inspection.count > 0
      ? mutationPlan('remove', null, allKeys, 0, quote?.reason || 'ineligible_cart')
      : mutationPlan('none', null, allKeys, 0, quote?.reason || 'ineligible_cart');
  }

  const effectiveOptIn = optedIn === true
    || inspection.internal.count > 0
    || inspection.legacy.count > 0;
  if (!effectiveOptIn) {
    return mutationPlan('none', expectedVariantId, allKeys, 1, 'not_selected');
  }
  if (inspection.internal.count === 0 && inspection.legacy.count === 0) {
    return mutationPlan('add', expectedVariantId, allKeys, 1, 'selected_missing', {
      removeLineKeys: [],
      addExpected: true,
    });
  }

  const singleInternal = inspection.internal.count === 1
    ? inspection.internal.lines[0]
    : null;
  const internalCorrect = singleInternal !== null
    && normalizeId(singleInternal.variant_id) === expectedVariantId
    && singleInternal.quantity === 1;

  if (internalCorrect && inspection.legacy.count === 0) {
    return mutationPlan('none', expectedVariantId, allKeys, 1, 'already_correct');
  }
  if (internalCorrect && inspection.legacy.count > 0) {
    return mutationPlan('cleanup', expectedVariantId, allKeys, 1, 'legacy_lines_present', {
      removeLineKeys: inspection.legacy.keys,
      addExpected: false,
    });
  }

  const reason = inspection.internal.count > 1
    ? 'multiple_internal_lines'
    : inspection.internal.count === 1 && singleInternal.quantity !== 1
      ? 'quantity_mismatch'
      : inspection.internal.count === 1
        ? 'wrong_variant'
        : 'legacy_only';
  return mutationPlan('normalize', expectedVariantId, allKeys, 1, reason, {
    removeLineKeys: allKeys,
    addExpected: true,
  });
}

function planProtectionMutation(input = {}) {
  return planShippingProtectionNormalization(input);
}

function ensureJsRoute(value, fallback) {
  const route = typeof value === 'string' && value !== '' ? value : fallback;
  return route.endsWith('.js') ? route : `${route}.js`;
}

function requestSectionsPayload(sections, sectionsUrl) {
  const names = Array.isArray(sections) ? sections.filter((value) => typeof value === 'string' && value !== '') : [];
  return names.length > 0 ? { sections: names, sections_url: sectionsUrl || '/' } : {};
}

function activeCartSections(documentRef = globalThis.document) {
  const sections = Array.from(documentRef?.querySelectorAll?.('cart-items-component[data-section-id]') || [])
    .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : null)
    .filter(Boolean);
  return [...new Set(sections)];
}

function captureCartViewState(element, documentRef = globalThis.document) {
  const cartItems = element?.closest?.('cart-items-component[data-section-id]');
  const scrollContainer = cartItems?.querySelector?.('.cart-drawer__content');
  const drawerInner = cartItems?.closest?.('.cart-drawer__inner');
  const activeElement = documentRef?.activeElement;
  return Object.freeze({
    cart_items: cartItems instanceof HTMLElement ? cartItems : null,
    scroll_top: scrollContainer instanceof HTMLElement ? scrollContainer.scrollTop : null,
    window_scroll_y: Number.isFinite(globalThis.window?.scrollY) ? globalThis.window.scrollY : null,
    active_element: activeElement instanceof HTMLElement ? activeElement : null,
    drawer_inner: drawerInner instanceof HTMLElement ? drawerInner : null,
    drawer_inner_scroll_top: drawerInner instanceof HTMLElement ? drawerInner.scrollTop : null,
  });
}

function replaceCartFragment(currentRoot, nextRoot, selector, documentRef) {
  const current = currentRoot.querySelector(selector);
  const next = nextRoot.querySelector(selector);
  if (!(current instanceof Element) || !(next instanceof Element)) return false;
  current.replaceWith(documentRef.importNode(next, true));
  return true;
}

function restoreCartViewPosition(viewState) {
  const scrollContainer = viewState?.cart_items?.querySelector?.('.cart-drawer__content');
  if (scrollContainer instanceof HTMLElement && Number.isFinite(viewState.scroll_top)) {
    const maximumScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    scrollContainer.scrollTop = Math.min(viewState.scroll_top, maximumScrollTop);
  } else if (
    Number.isFinite(viewState?.window_scroll_y)
    && typeof globalThis.window?.scrollTo === 'function'
  ) {
    globalThis.window.scrollTo({ top: viewState.window_scroll_y, left: globalThis.window.scrollX, behavior: 'auto' });
  }
  if (viewState?.drawer_inner instanceof HTMLElement && viewState.drawer_inner.isConnected) {
    viewState.drawer_inner.scrollTop = Number.isFinite(viewState.drawer_inner_scroll_top)
      ? viewState.drawer_inner_scroll_top
      : viewState.drawer_inner.scrollTop;
  }
}

function patchProtectionCartSection(viewState, sections, documentRef = globalThis.document) {
  const cartItems = viewState?.cart_items;
  const sectionId = cartItems?.dataset?.sectionId;
  const sectionHtml = sectionId ? sections?.[sectionId] : null;
  if (!(cartItems instanceof HTMLElement) || typeof sectionHtml !== 'string' || sectionHtml === '') return false;

  const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const nextCartItems = Array.from(parsed.querySelectorAll('cart-items-component[data-section-id]'))
    .find((element) => element instanceof HTMLElement && element.dataset.sectionId === sectionId);
  if (!(nextCartItems instanceof HTMLElement)) return false;

  const fragmentsUpdated = [
    replaceCartFragment(cartItems, nextCartItems, '.cart-items__wrapper', documentRef),
    replaceCartFragment(cartItems, nextCartItems, '.cart-totals', documentRef),
  ];
  if (fragmentsUpdated.some((updated) => updated !== true)) return false;

  const currentDrawer = cartItems.closest('#cart-drawer');
  const nextDrawer = nextCartItems.closest('#cart-drawer');
  const currentHeading = currentDrawer?.querySelector('#cart-drawer-heading');
  const nextHeading = nextDrawer?.querySelector('#cart-drawer-heading');
  if (currentHeading instanceof HTMLElement && nextHeading instanceof HTMLElement) {
    currentHeading.replaceChildren(...Array.from(nextHeading.childNodes).map((node) => documentRef.importNode(node, true)));
  }

  restoreCartViewPosition(viewState);
  if (viewState.active_element instanceof HTMLElement && viewState.active_element.isConnected) {
    viewState.active_element.focus({ preventScroll: true });
  }
  return true;
}

async function fetchWithTimeout(fetchImpl, url, options, timeoutMs = 8000) {
  if (typeof AbortController === 'undefined') return fetchImpl(url, options);
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchImpl(url, { ...options, signal: controller.signal });
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

async function ajaxJson(fetchImpl, url, body) {
  const response = await fetchWithTimeout(fetchImpl, url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response?.ok) throw new Error(`Protection cart mutation failed (${response?.status ?? 'network'})`);
  return response.json();
}

let activeMutation = null;
let activeMutationSignature = null;

async function executeProtectionPlan(plan, {
  config,
  fetchImpl = globalThis.fetch,
  routes = {},
  sections = ['cart-drawer-section', 'main-cart'],
  sectionsUrl = globalThis.location?.pathname || '/',
} = {}) {
  const runtimeConfig = normalizeProtectionRuntimeConfig(config);
  if (!runtimeConfig.enabled) return Object.freeze({ executed: false, reason: 'disabled' });
  if (!isObject(plan) || plan.action === 'none') {
    return Object.freeze({ executed: false, reason: plan?.reason || 'no_action' });
  }
  if (typeof fetchImpl !== 'function') throw new TypeError('A fetch implementation is required');

  const signature = JSON.stringify(plan);
  if (activeMutation) {
    return Object.freeze({
      executed: false,
      reason: activeMutationSignature === signature ? 'duplicate_in_flight' : 'mutation_in_progress',
    });
  }

  const root = String(globalThis.window?.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const addUrl = ensureJsRoute(routes.add || globalThis.window?.Theme?.routes?.cart_add_url, `${root}cart/add`);
  const changeUrl = ensureJsRoute(routes.change || globalThis.window?.Theme?.routes?.cart_change_url, `${root}cart/change`);
  const cartUrl = ensureJsRoute(routes.cart || globalThis.window?.Theme?.routes?.cart_url, `${root}cart`);
  const sectionPayload = requestSectionsPayload(sections, sectionsUrl);

  activeMutationSignature = signature;
  activeMutation = (async () => {
    let latestAjaxResult = null;
    const keys = Array.isArray(plan.remove_line_keys)
      ? [...new Set(plan.remove_line_keys.map(String))]
      : [];
    const removeKeys = async () => {
      for (const key of keys) {
        latestAjaxResult = await ajaxJson(fetchImpl, changeUrl, { id: key, quantity: 0, ...sectionPayload });
      }
    };
    const addExpected = async () => {
      if (!plan.expected_variant_id) throw new Error('Expected protection variant is missing');
      latestAjaxResult = await ajaxJson(fetchImpl, addUrl, {
        items: [{
          id: plan.expected_variant_id,
          quantity: 1,
          properties: { [PROTECTION_LINE_PROPERTY]: 'true' },
        }],
        ...sectionPayload,
      });
    };

    if (plan.action === 'add') {
      await addExpected();
    } else if (plan.action === 'remove') {
      await removeKeys();
    } else if (plan.action === 'cleanup') {
      await removeKeys();
    } else if (plan.action === 'normalize') {
      await removeKeys();
      if (plan.add_expected) await addExpected();
    } else {
      throw new Error(`Unsupported protection mutation action: ${plan.action}`);
    }

    const cartResponse = await fetchWithTimeout(fetchImpl, cartUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cartResponse?.ok) {
      throw new Error(`Protection cart refresh failed (${cartResponse?.status ?? 'network'})`);
    }
    const rawCart = await cartResponse.json();
    return Object.freeze({
      executed: true,
      reason: plan.reason,
      action: plan.action,
      sections: Object.freeze(isObject(latestAjaxResult?.sections) ? { ...latestAjaxResult.sections } : {}),
      raw_cart: isObject(rawCart) ? rawCart : null,
      item_count: Number.isSafeInteger(rawCart?.item_count) ? rawCart.item_count : null,
    });
  })();

  try {
    return await activeMutation;
  } finally {
    activeMutation = null;
    activeMutationSignature = null;
  }
}

let normalizationFlight = null;

async function normalizeShippingProtectionLines({
  contract = null,
  config = null,
  optedIn,
  voluntarilyDisabled = false,
  refresh = false,
  verify = false,
  fetchImpl = globalThis.fetch,
  sections = activeCartSections(),
  sectionsUrl = globalThis.location?.pathname || '/',
} = {}) {
  if (normalizationFlight) return normalizationFlight;

  normalizationFlight = (async () => {
    const runtimeData = isObject(config) ? config : readInitialProtectionData();
    let latestContract = contract || globalThis.window?.PokeGeekCartContract?.getCurrentCartContract?.() || null;

    if (refresh && typeof globalThis.window?.PokeGeekCartContract?.refreshCurrentCartContract === 'function') {
      latestContract = await globalThis.window.PokeGeekCartContract.refreshCurrentCartContract(
        'shipping-protection-normalize'
      );
    }

    const firstInspection = inspectShippingProtectionLines(latestContract, runtimeData);
    const effectiveOptIn = typeof optedIn === 'boolean' ? optedIn : firstInspection.count > 0;
    let lastResult = Object.freeze({ executed: false, reason: 'already_normalized' });
    const maximumAttempts = verify ? 2 : 1;

    for (let attempt = 0; attempt < maximumAttempts; attempt += 1) {
      const plan = planShippingProtectionNormalization({
        contract: latestContract,
        config: runtimeData,
        optedIn: effectiveOptIn,
        voluntarilyDisabled,
        mutationInProgress: false,
      });
      if (plan.action === 'none') {
        return Object.freeze({
          ...lastResult,
          verified: true,
          plan,
          contract: latestContract,
        });
      }

      lastResult = await executeProtectionPlan(plan, {
        config: runtimeData,
        fetchImpl,
        sections,
        sectionsUrl,
      });

      if (!verify) {
        return Object.freeze({
          ...lastResult,
          verified: false,
          plan,
          contract: latestContract,
        });
      }

      if (typeof globalThis.window?.PokeGeekCartContract?.refreshCurrentCartContract !== 'function') {
        throw new Error('Cart contract refresh is unavailable');
      }
      latestContract = await globalThis.window.PokeGeekCartContract.refreshCurrentCartContract(
        'shipping-protection-verify'
      );
    }

    const finalPlan = planShippingProtectionNormalization({
      contract: latestContract,
      config: runtimeData,
      optedIn: effectiveOptIn,
      voluntarilyDisabled,
      mutationInProgress: false,
    });
    if (finalPlan.action !== 'none') throw new Error(`Protection normalization incomplete (${finalPlan.reason})`);

    return Object.freeze({
      ...lastResult,
      verified: true,
      plan: finalPlan,
      contract: latestContract,
    });
  })();

  try {
    return await normalizationFlight;
  } finally {
    normalizationFlight = null;
  }
}

function dispatchProtectionError(error, documentRef = globalThis.document) {
  console.warn('[pg-shipping-protection] Verification blocked:', error);
  documentRef?.dispatchEvent?.(new CustomEvent('pokegeek:shipping-protection-error', {
    detail: Object.freeze({ code: 'normalization_failed' }),
  }));
}

function readInitialProtectionData(documentRef = globalThis.document) {
  const node = documentRef?.getElementById?.(INITIAL_DATA_ID);
  if (!node?.textContent) return {};
  try {
    const parsed = JSON.parse(node.textContent);
    return isObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizedLocale(value) {
  const locale = String(value || 'fr').toLowerCase().split('-')[0];
  return Object.hasOwn(TRANSLATIONS, locale) ? locale : 'fr';
}

function formatEuroMinorUnits(value, locale) {
  if (!Number.isSafeInteger(value) || value < 0) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function previewContextAuthorized(element, documentRef = globalThis.document) {
  if (element?.dataset?.previewAuthorized === 'true') return true;
  if (globalThis.window?.Shopify?.designMode === true) return true;
  return Boolean(documentRef?.getElementById?.('PBarNextFrame'));
}

function decideContractEvent(latestSequence, latestSignature, event) {
  const sequence = Number.isSafeInteger(event?.detail?.sequence) ? event.detail.sequence : null;
  const contract = event?.detail?.contract;
  const signature = isObject(contract) ? JSON.stringify(contract) : null;
  if (sequence !== null && sequence <= latestSequence) {
    return Object.freeze({
      accepted: false,
      reason: 'stale_or_repeated_sequence',
      sequence: latestSequence,
      signature: latestSignature,
    });
  }
  if (signature !== null && signature === latestSignature) {
    return Object.freeze({
      accepted: false,
      reason: 'repeated_contract',
      sequence: latestSequence,
      signature: latestSignature,
    });
  }
  return Object.freeze({
    accepted: true,
    reason: 'new_contract',
    sequence: sequence ?? latestSequence,
    signature,
  });
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class PokeGeekShippingProtection extends HTMLElementBase {
  #config = normalizeProtectionRuntimeConfig({});
  #contract = null;
  #locale = 'fr';
  #optedIn = false;
  #voluntarilyDisabled = false;
  #latestSequence = -1;
  #latestSignature = null;
  #connected = false;
  #previewObserver = null;
  #mutationInProgress = false;
  #pendingViewState = null;
  #lastError = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;
    this.#config = normalizeProtectionRuntimeConfig(readInitialProtectionData());
    this.#locale = normalizedLocale(this.dataset.locale);
    this.addEventListener('change', this.#handleToggle);
    this.addEventListener('pointerdown', this.#captureInteractionViewState, true);
    document.addEventListener(PROTECTION_EVENT, this.#handleContractUpdate);
    document.addEventListener('pokegeek:shipping-protection-error', this.#handleProtectionError);
    this.#contract = window.PokeGeekCartContract?.getCurrentCartContract?.() || null;
    const initialInspection = inspectShippingProtectionLines(this.#contract, this.#config);
    if (initialInspection.count > 0) this.#optedIn = true;
    this.#watchForPreviewAuthorization();
    this.#render();
    this.#synchronizeFutureMutation();
  }

  disconnectedCallback() {
    if (!this.#connected) return;
    this.#connected = false;
    this.removeEventListener('change', this.#handleToggle);
    this.removeEventListener('pointerdown', this.#captureInteractionViewState, true);
    document.removeEventListener(PROTECTION_EVENT, this.#handleContractUpdate);
    document.removeEventListener('pokegeek:shipping-protection-error', this.#handleProtectionError);
    this.#previewObserver?.disconnect();
    this.#previewObserver = null;
  }

  #watchForPreviewAuthorization() {
    if (!this.#config.preview_only || previewContextAuthorized(this)) return;
    if (typeof MutationObserver === 'undefined' || !document.documentElement) return;
    this.#previewObserver = new MutationObserver(() => {
      if (!previewContextAuthorized(this)) return;
      this.#previewObserver?.disconnect();
      this.#previewObserver = null;
      this.#render();
    });
    this.#previewObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  #handleContractUpdate = (event) => {
    const decision = decideContractEvent(this.#latestSequence, this.#latestSignature, event);
    if (!decision.accepted) return;
    this.#latestSequence = decision.sequence;
    this.#latestSignature = decision.signature;
    this.#contract = event?.detail?.contract;

    const inspection = inspectShippingProtectionLines(this.#contract, this.#config);
    if (!this.#voluntarilyDisabled && inspection.count > 0) this.#optedIn = true;
    this.#render();
    this.#synchronizeFutureMutation();
  };

  #captureInteractionViewState = (event) => {
    const choice = event.target?.closest?.('.pg-shipping-protection__choice');
    if (choice && this.contains(choice)) this.#pendingViewState = captureCartViewState(this);
  };

  #handleProtectionError = () => {
    this.#lastError = true;
    this.#mutationInProgress = false;
    this.#render();
  };

  #handleToggle = (event) => {
    const toggle = event.target;
    if (!toggle?.matches?.('[data-protection-toggle]') || !this.contains(toggle)) return;
    if (!this.#config.enabled) {
      toggle.checked = false;
      return;
    }
    this.#optedIn = toggle.checked === true;
    this.#voluntarilyDisabled = !this.#optedIn;
    this.#lastError = false;
    const interactionViewState = this.#pendingViewState || captureCartViewState(this);
    restoreCartViewPosition(interactionViewState);
    toggle.focus({ preventScroll: true });
    this.#pendingViewState = Object.freeze({
      ...interactionViewState,
      active_element: toggle,
    });
    this.#mutationInProgress = true;
    this.#render();
    this.#synchronizeFutureMutation();
  };

  async #synchronizeFutureMutation() {
    const plan = planShippingProtectionNormalization({
      contract: this.#contract,
      config: this.#config,
      optedIn: this.#optedIn,
      voluntarilyDisabled: this.#voluntarilyDisabled,
      mutationInProgress: activeMutation !== null,
    });
    if (plan.action === 'none') {
      if (this.#mutationInProgress) {
        this.#mutationInProgress = false;
        this.#render();
      }
      return;
    }
    this.#mutationInProgress = true;
    this.#render();
    const viewState = this.#pendingViewState || captureCartViewState(this);
    const eventTarget = viewState.cart_items || this;
    const deferredEvent = CartLinesUpdateEvent.createPromise();
    const cartUpdateEvent = new CartLinesUpdateEvent({
      action: plan.action === 'add'
        ? 'add'
        : ['remove', 'cleanup'].includes(plan.action)
          ? 'remove'
          : 'update',
      context: 'shipping-protection',
      lines: [],
      promise: deferredEvent.promise,
    });
    try {
      const result = await normalizeShippingProtectionLines({
        contract: this.#contract,
        config: this.#config,
        optedIn: this.#optedIn,
        voluntarilyDisabled: this.#voluntarilyDisabled,
        sections: activeCartSections(),
      });
      this.#lastError = false;
      const patchedWithoutScrollReplacement = patchProtectionCartSection(viewState, result.sections);
      (patchedWithoutScrollReplacement ? eventTarget : this).dispatchEvent(cartUpdateEvent);
      deferredEvent.resolve({
        cart: null,
        detail: {
          itemCount: result.item_count,
          items: Array.isArray(result.raw_cart?.items) ? result.raw_cart.items : undefined,
          rawCart: result.raw_cart,
          sections: result.sections,
          source: 'pg-shipping-protection',
          didError: false,
        },
      });
    } catch (error) {
      deferredEvent.reject(error);
      const inspection = inspectShippingProtectionLines(this.#contract, this.#config);
      this.#optedIn = inspection.count > 0;
      this.#voluntarilyDisabled = false;
      this.#lastError = true;
      console.warn('[pg-shipping-protection] Mutation skipped:', error);
    } finally {
      this.#pendingViewState = null;
      this.#mutationInProgress = false;
      this.#render();
    }
  }

  #render() {
    const previewAuthorized = previewContextAuthorized(this);
    const canRender = this.#config.enabled || (this.#config.preview_only && previewAuthorized);
    const quote = isObject(this.#contract?.protection_quote) ? this.#contract.protection_quote : null;
    const eligibleSubtotal = this.#contract?.protection_eligible_subtotal;
    const hiddenForCart = this.#contract?.is_empty === true || !Number.isSafeInteger(eligibleSubtotal) || eligibleSubtotal <= 0;
    const hiddenForCurrency = quote?.reason === 'unsupported_currency';
    this.dataset.loading = String(this.#mutationInProgress);
    this.setAttribute('aria-busy', String(this.#mutationInProgress));

    if (!canRender || hiddenForCart || hiddenForCurrency) {
      this.hidden = true;
      return;
    }

    const text = TRANSLATIONS[this.#locale];
    const title = this.querySelector('[data-protection-title]');
    const short = this.querySelector('[data-protection-short]');
    const detailsLabel = this.querySelector('[data-protection-details-label]');
    const detailsText = this.querySelector('[data-protection-details-text]');
    const preview = this.querySelector('[data-protection-preview]');
    const price = this.querySelector('[data-protection-price]');
    const unavailable = this.querySelector('[data-protection-unavailable]');
    const toggle = this.querySelector('[data-protection-toggle]');

    if (title) title.textContent = text.title;
    if (short) short.textContent = text.short;
    if (detailsLabel) detailsLabel.textContent = text.details_label;
    if (detailsText) detailsText.textContent = text.details;
    if (preview) {
      preview.textContent = this.#config.enabled ? '' : text.preview;
      preview.hidden = this.#config.enabled;
    }

    const supported = quote?.supported === true && Number.isSafeInteger(quote?.quoted_price);
    if (price) price.textContent = supported ? formatEuroMinorUnits(quote.quoted_price, this.#locale) : '';
    if (unavailable) {
      unavailable.hidden = supported && !this.#lastError;
      unavailable.textContent = this.#lastError
        ? text.mutation_error
        : quote?.reason === 'above_supported_range'
          ? text.unavailable_range
          : text.unavailable_generic;
    }
    if (toggle) {
      toggle.disabled = this.#mutationInProgress || !this.#config.enabled || !supported;
      toggle.checked = this.#config.enabled && supported && this.#optedIn;
      toggle.setAttribute('aria-disabled', String(toggle.disabled));
    }

    this.dataset.state = supported ? 'eligible' : 'unavailable';
    this.hidden = false;
  }
}

const checkoutBypassTargets = new WeakSet();

function checkoutFormFromEvent(event) {
  const form = event?.target;
  if (!form || String(form.tagName || '').toLowerCase() !== 'form') return null;
  const action = String(form.getAttribute?.('action') || '');
  const submitterName = String(event?.submitter?.getAttribute?.('name') || '');
  return /\/cart(?:\.js)?(?:\?|$)/.test(action) || submitterName === 'checkout' ? form : null;
}

function acceleratedCheckoutTarget(event) {
  const path = typeof event?.composedPath === 'function' ? event.composedPath() : [event?.target];
  return path.find((node) => node?.matches?.(
    'shopify-accelerated-checkout-cart, .shopify-payment-button__button, [data-shopify-pay-button]'
  )) || null;
}

function installCheckoutProtectionGuard(documentRef = globalThis.document) {
  if (!documentRef || documentRef.documentElement?.dataset?.pgProtectionCheckoutGuard === 'true') return;
  if (documentRef.documentElement) documentRef.documentElement.dataset.pgProtectionCheckoutGuard = 'true';

  documentRef.addEventListener('submit', (event) => {
    const form = checkoutFormFromEvent(event);
    if (!form) return;
    if (checkoutBypassTargets.has(form)) {
      checkoutBypassTargets.delete(form);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    normalizeShippingProtectionLines({ refresh: true, verify: true })
      .then(() => {
        checkoutBypassTargets.add(form);
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else form.submit();
      })
      .catch((error) => dispatchProtectionError(error, documentRef));
  }, true);

  documentRef.addEventListener('click', (event) => {
    const target = acceleratedCheckoutTarget(event);
    if (!target) return;
    if (checkoutBypassTargets.has(target)) {
      checkoutBypassTargets.delete(target);
      return;
    }
    const currentPlan = planShippingProtectionNormalization({
      contract: globalThis.window?.PokeGeekCartContract?.getCurrentCartContract?.() || null,
      config: readInitialProtectionData(documentRef),
      mutationInProgress: normalizationFlight !== null,
    });
    if (currentPlan.action === 'none') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    normalizeShippingProtectionLines({ refresh: true, verify: true })
      .then(() => {
        checkoutBypassTargets.add(target);
        target.click();
      })
      .catch((error) => dispatchProtectionError(error, documentRef));
  }, true);

  documentRef.addEventListener('toggle', (event) => {
    const drawer = event.target;
    if (!drawer?.matches?.('#cart-drawer, cart-drawer, dialog[data-cart-drawer]')) return;
    if (!(drawer.open === true || drawer.hasAttribute?.('open'))) return;
    normalizeShippingProtectionLines({ refresh: true }).catch((error) => dispatchProtectionError(error, documentRef));
  }, true);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('pg-shipping-protection')) {
    customElements.define('pg-shipping-protection', PokeGeekShippingProtection);
  }
  installCheckoutProtectionGuard();
  globalThis.queueMicrotask?.(() => {
    normalizeShippingProtectionLines().catch((error) => dispatchProtectionError(error));
  });
}

export {
  PROTECTION_EVENT,
  TRANSLATIONS,
  decideContractEvent,
  executeProtectionPlan,
  expectedProtectionVariantId,
  formatEuroMinorUnits,
  inspectShippingProtectionLines,
  inspectProtectionLines,
  installCheckoutProtectionGuard,
  isInternalProtectionLine,
  isLegacyProtectionLine,
  normalizeShippingProtectionLines,
  normalizeProtectionRuntimeConfig,
  normalizeProtectionUiConfig,
  planShippingProtectionNormalization,
  planProtectionMutation,
  previewContextAuthorized,
};
